import { FC, useEffect, useState } from "react";
import { Button, Modal, Select, Space, Spin, Tabs, message } from "antd";

import {
    AnalysisOutput,
    AnalysisOutputType,
    DealAnalysisOutput,
    FlexibleAnalysisOutput,
    Project,
    ProjectAnalysis,
    ProjectAnalysisStatus,
    RfxAnalysisOutput,
    StructuredOutputType,
    isClosingAnalysis,
    isDealOutput,
    isFlexibleOutput,
    isRfxOutput,
    isRfxProject,
} from "../../../types/ProjectTypes";
import { getAnalysisOutput, saveAnalysisOutput } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { DealInfoTab } from "./ModalSaveAnalysisOutput/DealInfoTab";
import { SummaryTab } from "./ModalSaveAnalysisOutput/SummaryTab";
import { DealReportTab } from "./ModalSaveAnalysisOutput/DealReportTab";
import { RfxReportTab } from "./ModalSaveAnalysisOutput/RfxReportTab";
import { RfxVendorsTab } from "./ModalSaveAnalysisOutput/RfxVendorsTab";
import { LeversTab } from "./ModalSaveAnalysisOutput/LeversTab";
import { FlexibleContentTab } from "./ModalSaveAnalysisOutput/FlexibleContentTab";
import { AttachmentsTab } from "./ModalSaveAnalysisOutput/AttachmentsTab";
import { FlexibleStructuredOutputsTab } from "./ModalSaveAnalysisOutput/FlexibleStructuredOutputsTab";

import { ProjectViewState } from "../ProjectViewState";

interface ModalSaveAnalysisOutputProps {
    open: boolean;
    state: ProjectViewState;
    onClose: () => void;
    onSuccess: () => void | Promise<void>;
}

function initDealOutput(
    project: Project,
    existing: DealAnalysisOutput | undefined
): DealAnalysisOutput {
    const initial: DealAnalysisOutput = {
        type: AnalysisOutputType.DEAL,
        summary: existing?.summary,
        report: existing?.report,
        levers: existing?.levers && existing.levers.length > 0 ? existing.levers : undefined,
        sourceUrls: existing?.sourceUrls,
    };

    // Deal info: prefer saved output, fall back to orderForm for v2+
    if (existing?.dealInfo) {
        initial.dealInfo = existing.dealInfo;
    } else if (project.orderForm) {
        initial.dealInfo = {
            vendorKey: project.orderForm.vendorKey,
            value: Math.round(project.orderForm.value * 100),
            orderStartDate: project.orderForm.orderStartDate?.toString(),
            orderEndDate: project.orderForm.orderEndDate?.toString(),
            orderScore: project.orderForm.orderScore,
            optimizedScore: project.orderForm.optimizedScore,
            autoRenewal: project.orderForm.autoRenewal,
            ownerId: project.orderForm.ownerId,
            increase:
                project.orderForm.increase != null
                    ? Math.round(project.orderForm.increase * 100)
                    : undefined,
        };
    }

    // Pre-populate owner from project owner when not already set
    if (!initial.dealInfo?.ownerId && project.ownerId) {
        initial.dealInfo = {
            ...initial.dealInfo,
            ownerId: project.ownerId,
        } as NonNullable<DealAnalysisOutput["dealInfo"]>;
    }

    return initial;
}

function initRfxOutput(existing: RfxAnalysisOutput | undefined): RfxAnalysisOutput {
    return {
        type: AnalysisOutputType.RFX,
        report: existing?.report,
        sourceUrls: existing?.sourceUrls,
        vendors: existing?.vendors,
    };
}

function initFlexibleOutput(existing: FlexibleAnalysisOutput | undefined): FlexibleAnalysisOutput {
    return {
        type: AnalysisOutputType.FLEXIBLE,
        content: existing?.content || "",
        structuredOutputs: existing?.structuredOutputs,
    };
}

function defaultOutputType(project: Project, analysis: ProjectAnalysis): AnalysisOutputType {
    if (isClosingAnalysis(analysis)) {
        return AnalysisOutputType.FLEXIBLE;
    } else if (isRfxProject(project)) {
        return AnalysisOutputType.RFX;
    } else {
        return AnalysisOutputType.DEAL;
    }
}

function initOutputForType(
    outputType: AnalysisOutputType,
    project: Project,
    existingOutput?: AnalysisOutput
): AnalysisOutput {
    switch (outputType) {
        case AnalysisOutputType.DEAL:
            return initDealOutput(
                project,
                isDealOutput(existingOutput) ? existingOutput : undefined
            );
        case AnalysisOutputType.RFX:
            return initRfxOutput(isRfxOutput(existingOutput) ? existingOutput : undefined);
        case AnalysisOutputType.FLEXIBLE:
            return initFlexibleOutput(
                isFlexibleOutput(existingOutput) ? existingOutput : undefined
            );
    }
}

const outputTypeOptions = [
    { label: "Deal", value: AnalysisOutputType.DEAL },
    { label: "RFX", value: AnalysisOutputType.RFX },
    { label: "Flexible", value: AnalysisOutputType.FLEXIBLE },
];

function useAnalysisOutputState({
    open,
    state,
    onClose,
    onSuccess,
}: ModalSaveAnalysisOutputProps) {
    const { project, analysis } = state;
    const [output, setOutput] = useState<AnalysisOutput>(
        initOutputForType(defaultOutputType(project, analysis), project)
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<"draft" | "complete" | null>(null);

    const resetState = () => {
        setOutput(initOutputForType(defaultOutputType(project, analysis), project));
        setSaving(null);
    };

    // Re-initialize output state only when the modal opens (not on every project re-fetch,
    // which would discard in-progress edits — e.g. after uploading an attachment).
    // Fetches the existing analysis output from the server. If there is none yet
    // (e.g. a fresh analysis), falls back to defaults.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        // Reset to defaults synchronously so the form has a sensible shape while
        // the fetch is in flight; the fetched data overwrites it on success.
        setOutput(initOutputForType(defaultOutputType(project, analysis), project));
        getAnalysisOutput(project.id, analysis.version).then(
            (existing) => {
                if (cancelled) return;
                const outputType = existing?.type ?? defaultOutputType(project, analysis);
                setOutput(initOutputForType(outputType, project, existing));
                setLoading(false);
            },
            () => {
                // No existing output (fresh analysis): defaults already applied above.
                if (cancelled) return;
                setLoading(false);
            }
        );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, analysis.version]);

    const handleOutputTypeChange = (newType: AnalysisOutputType) => {
        if (newType === output.type) return;
        setOutput(initOutputForType(newType, project));
    };

    const handleSave = async (draft: boolean) => {
        if (!draft) {
            // Validation only required when sharing with user
            if (isRfxOutput(output)) {
                if (!output.report?.content) {
                    message.error("Cannot share with user: the report is blank");
                    return;
                }
            } else if (isDealOutput(output)) {
                if (!output.dealInfo?.vendorKey) {
                    message.error("Please select a vendor");
                    return;
                }
                if (!output.report) {
                    message.error("Cannot share with user: the report is blank");
                    return;
                }
            } else if (isFlexibleOutput(output)) {
                for (const so of output.structuredOutputs ?? []) {
                    if (so.type === StructuredOutputType.DEAL && !so.data.dealInfo?.vendorKey) {
                        message.error("Please select a vendor");
                        return;
                    }
                }
                if (!output.content) {
                    message.error("Cannot share with user: content is blank");
                    return;
                }
            }
        }

        setSaving(draft ? "draft" : "complete");
        try {
            await saveAnalysisOutput(project.id, analysis.version, { draft, output });
            message.success(draft ? "Analysis saved" : "Analysis completed and shared with user");
            await onSuccess();
            onClose();
        } catch (error) {
            handleError(error);
        } finally {
            setSaving(null);
        }
    };

    return { output, setOutput, loading, saving, handleSave, handleOutputTypeChange, resetState };
}

export const ModalSaveAnalysisOutput: FC<ModalSaveAnalysisOutputProps> = (props) => {
    const { open, state, onClose } = props;
    const { project, analysis } = state;
    const { output, setOutput, loading, saving, handleSave, handleOutputTypeChange, resetState } =
        useAnalysisOutputState(props);
    const defaultTab = isFlexibleOutput(output)
        ? "content"
        : isRfxOutput(output)
          ? "report"
          : "deal";
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const alreadyShared = analysis.status === ProjectAnalysisStatus.COMPLETED;

    // Refresh attachments by calling onSuccess (re-fetches project)
    const refreshAttachments = async () => {
        await props.onSuccess();
    };

    const handleClose = () => {
        setShowCloseConfirm(true);
    };

    const handleConfirmDiscard = () => {
        setShowCloseConfirm(false);
        setActiveTab(defaultTab);
        resetState();
        onClose();
    };

    const handleConfirmSave = async () => {
        setShowCloseConfirm(false);
        await handleSave(alreadyShared ? false : true);
    };

    const handleConfirmGoBack = () => {
        setShowCloseConfirm(false);
    };

    const getTabItems = () => {
        const attachmentsTab = {
            key: "attachments",
            label: `Attachments (${analysis.attachments?.length ?? 0})`,
            children: (
                <AttachmentsTab
                    projectId={project.id}
                    analysisVersion={analysis.version}
                    attachments={analysis.attachments || []}
                    onAttachmentsChange={refreshAttachments}
                />
            ),
        };

        if (isFlexibleOutput(output)) {
            return [
                {
                    key: "content",
                    label: "Content",
                    children: <FlexibleContentTab output={output} onChange={setOutput} />,
                },
                attachmentsTab,
                {
                    key: "structured",
                    label: `Structured (${output.structuredOutputs?.length ?? 0})`,
                    children: (
                        <FlexibleStructuredOutputsTab
                            output={output}
                            onChange={setOutput}
                            projectType={project.type}
                        />
                    ),
                },
            ];
        }

        if (isRfxOutput(output)) {
            return [
                {
                    key: "report",
                    label: "Report",
                    children: <RfxReportTab output={output} onChange={setOutput} />,
                },
                {
                    key: "vendors",
                    label: `Vendors (${output.vendors?.length ?? 0})`,
                    children: <RfxVendorsTab output={output} onChange={setOutput} />,
                },
                attachmentsTab,
            ];
        }

        // Deal output
        return [
            {
                key: "deal",
                label: "Deal info",
                children: <DealInfoTab output={output} onChange={setOutput} />,
            },
            {
                key: "summary",
                label: "Summary",
                children: <SummaryTab output={output} onChange={setOutput} />,
            },
            {
                key: "report",
                label: "Report",
                children: (
                    <DealReportTab
                        projectId={project.id}
                        analysisVersion={analysis.version}
                        output={output}
                        onChange={setOutput}
                    />
                ),
            },
            {
                key: "levers",
                label: `Levers (${output.levers?.length ?? 0})`,
                children: <LeversTab output={output} onChange={setOutput} />,
            },
            attachmentsTab,
        ];
    };

    // Reset active tab when output type changes
    useEffect(() => {
        if (isFlexibleOutput(output)) {
            setActiveTab("content");
        } else if (isRfxOutput(output)) {
            setActiveTab("report");
        } else {
            setActiveTab("deal");
        }
    }, [output.type]);

    return (
        <>
            <Modal
                title="Save analysis output"
                open={open}
                onCancel={handleClose}
                width="calc(100vw - 48px)"
                footer={[
                    <Button key="cancel" onClick={handleClose} disabled={!!saving}>
                        Cancel
                    </Button>,
                    ...(!alreadyShared
                        ? [
                              <Button
                                  key="save"
                                  onClick={() => handleSave(true)}
                                  loading={saving === "draft"}
                                  disabled={loading || saving === "complete"}
                              >
                                  Save
                              </Button>,
                              <Button
                                  key="complete"
                                  type="primary"
                                  onClick={() => handleSave(false)}
                                  loading={saving === "complete"}
                                  disabled={loading || saving === "draft"}
                              >
                                  Save and share with user
                              </Button>,
                          ]
                        : [
                              <Button
                                  key="complete"
                                  type="primary"
                                  onClick={() => handleSave(false)}
                                  loading={saving === "complete"}
                                  disabled={loading}
                              >
                                  Save and share with user
                              </Button>,
                          ]),
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Output type:</span>
                        <Select
                            value={output.type}
                            onChange={handleOutputTypeChange}
                            options={outputTypeOptions}
                            style={{ width: 160 }}
                            size="small"
                            disabled={loading}
                        />
                    </div>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "48px 0" }}>
                            <Spin />
                        </div>
                    ) : (
                        <Tabs activeKey={activeTab} items={getTabItems()} onChange={setActiveTab} />
                    )}
                </Space>
            </Modal>

            {/* Close Confirmation Modal */}
            <Modal
                title="Unsaved changes"
                open={showCloseConfirm}
                onCancel={handleConfirmGoBack}
                footer={[
                    <Button key="discard" danger onClick={handleConfirmDiscard}>
                        Discard
                    </Button>,
                    <Button key="goback" onClick={handleConfirmGoBack}>
                        Go Back
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleConfirmSave}
                        loading={!!saving}
                    >
                        Save
                    </Button>,
                ]}
            >
                Do you want to save your changes before closing?
            </Modal>
        </>
    );
};
